import React from 'react';
import { Button, ListItem, ListItemText, Box, List } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import SettingsPanelSection from '../SettingsPanelSection';
import { connect } from 'react-redux';
import { createActions } from '../../store';
import SimpleStepper from '../../../../misc/SimpleStepper';
import CheckedBoxIcon from '@mui/icons-material/CheckBoxRounded';
import UncheckedBoxIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import ImageIcon from '@mui/icons-material/ImageRounded';
import PresetIcon from '@mui/icons-material/LayersRounded';
import CollapsibleListItem from '../../../../list-items/CollapsibleListItem/CollapsibleListItem';
import CircleProgressButton from '../../../../buttons/CircleProgressButton';
import TooltipHelpButton from '../../../../buttons/TooltipHelpButton/TooltipHelpButton';
import CreatePageConstants from '../../CreatePageConstants';
import WaveLayersEditor from '../../components/WaveLayersEditor/WaveLayersEditor';
import TextElementsEditor from '../../components/TextElementsEditor';
import SettingsPanelAudioButton from '../SettingsPanelAudioButton';
import SubjectRoundedIcon from '@mui/icons-material/SubjectRounded';
import { VISUALIZER_SHAPE } from '../../Preview/PreviewDisplayConstants';
import { mediaPickerActions } from '../../MediaPickers/store';
import LogoMediaPickerDialog from '../../MediaPickers/LogoMediaPickerDialog';
import BackgroundMediaPickerDialog from '../../MediaPickers/BackgroundMediaPickerDialog';
import YouTubeCtaListItem from '../../components/VideoElementsEditor/YouTubeCta/YouTubeCtaListItem';
import ParticlesListItem from '../../components/VideoElementsEditor/Particles/ParticlesListItem';
import BackdropReflectionToggleListItem from '../../components/BackdropEditor/inputs/reflection/BackdropReflectionToggleListItem';
import { useNavigate } from 'react-router';

const styles = theme => ({
    actionButton: {
        width: 145
    },
    lyricsList: {
        marginBottom: theme.spacing(1)
    },
    '@media (max-resolution: 100dpi)': {
        actionButton: {
            width: 155
        },
    },
});

const SettingsPanelSectionGeneral = (props) => {
    const {
        showAdvancedSettings,
        setShowAdvancedSettings,
        visualizerShape,
        openExportDialog,
        setProperty,
        classes,
        save,
        openLogoPicker,
        openBackgroundPicker,
        videoDirty,
        audioDirty,
        loggedIn,
        savingInProgress,
        showCriticalOperationProgressWarning,
    } = props;

    const navigate = useNavigate();

    const toggleAdvancedSettings = () => {
        setShowAdvancedSettings(!showAdvancedSettings);
    };

    const renderPresetListItem = () => (
        <ListItem>
            <ListItemText>Preset</ListItemText>
            <Box>
                <Button
                    variant='outlined'
                    color='inherit'
                    size='small'
                    startIcon={<PresetIcon />}
                    onClick={() => setProperty('presetDialogOpen', true)}
                    className={classes.actionButton}
                >
                    Select Preset
                </Button>
            </Box>
        </ListItem>
    );

    const renderAudioListItem = () => (
        <ListItem>
            <ListItemText>
                Audio
                <TooltipHelpButton title={`Accepts MP3, WAV, or AAC.`} />
            </ListItemText>
            <Box>
                <SettingsPanelAudioButton save={save} className={classes.actionButton} />
            </Box>
        </ListItem>
    );

    const renderLogoImageListItem = () => (
        <ListItem>
            <ListItemText>Logo</ListItemText>
            <Box>
                <CircleProgressButton
                    variant='outlined'
                    color='inherit'
                    size='small'
                    startIcon={<ImageIcon />}
                    onClick={openLogoPicker}
                    className={classes.actionButton}
                >
                    Select Media
                </CircleProgressButton>
            </Box>
        </ListItem>
    );

    const renderBackgroundImageListItem = () => (
        <>
            <ListItem>
                <ListItemText>Background</ListItemText>
                <Box>
                    <CircleProgressButton
                        variant='outlined'
                        color='inherit'
                        size='small'
                        startIcon={<ImageIcon />}
                        onClick={openBackgroundPicker}
                        className={classes.actionButton}
                    >
                        Select Media
                    </CircleProgressButton>
                </Box>
            </ListItem>
            {!showAdvancedSettings && <BackdropReflectionToggleListItem />}
        </>
    );

    const renderLyricsItem = () => (
        <List className={classes.lyricsList} dense disablePadding>
            <ListItem>
                <ListItemText>Lyrics</ListItemText>
                <Box>
                    <Button
                        variant='outlined'
                        size='small'
                        startIcon={<SubjectRoundedIcon />}
                        onClick={() =>
                            setProperty(
                                'currentSettingsPanel',
                                CreatePageConstants.SETTINGS_PANELS.LYRICS
                            )
                        }
                        className={classes.actionButton}
                    >
                        Add Lyrics
                    </Button>
                </Box>
            </ListItem>
        </List>
    );

    const renderSettingsList = () => (
        <List dense disablePadding>
            {renderPresetListItem()}
            {visualizerShape === VISUALIZER_SHAPE.CIRCLE && renderLogoImageListItem()}
            {renderBackgroundImageListItem()}
            {renderAudioListItem()}
            <CollapsibleListItem text={'Text'}>
                <TextElementsEditor advanced={false} />
                {renderLyricsItem()}
            </CollapsibleListItem>
            <CollapsibleListItem text={'Colors'}>
                <WaveLayersEditor />
            </CollapsibleListItem>
            <YouTubeCtaListItem advanced={false} />
            <ParticlesListItem advanced={false} />
        </List>
    );

    const renderSettingsStepper = () => (
        <SimpleStepper
            orientation='vertical'
            nonLinear
            onStepChangeVerification={onStepChangeVerification}
            onActiveStepChange={onActiveStepChange}
            steps={[
                {
                    label: 'Preset',
                    content: <List dense disablePadding>{renderPresetListItem()}</List>,
                },
                {
                    label: 'Audio',
                    content: <List dense disablePadding>{renderAudioListItem()}</List>,
                },
                {
                    label: 'Images',
                    content: (
                        <List dense disablePadding>
                            {visualizerShape === VISUALIZER_SHAPE.CIRCLE && renderLogoImageListItem()}
                            {renderBackgroundImageListItem()}
                        </List>
                    ),
                },
                {
                    label: 'Text',
                    content: (
                        <>
                            <TextElementsEditor advanced={false} />
                            {renderLyricsItem()}
                        </>
                    ),
                },
                {
                    label: 'Colors',
                    content: <WaveLayersEditor />,
                },
                {
                    label: 'Elements',
                    content: (
                        <List dense disablePadding>
                            <YouTubeCtaListItem advanced={false} />
                            <ParticlesListItem advanced={false} />
                        </List>
                    ),
                },
                {
                    label: 'Export',
                    content: (
                        <Button
                            variant='outlined'
                            disabled={savingInProgress}
                            onClick={() => openExportDialog(navigate)}
                            color='primary'
                        >
                            Export Video
                        </Button>
                    ),
                },
            ]}
        />
    );

    const onStepChangeVerification = () => {
        const anonymousUser = !Boolean(loggedIn);
        const settingsUntouched = !videoDirty && !audioDirty;

        if (anonymousUser || settingsUntouched || !savingInProgress) return true;

        showCriticalOperationProgressWarning();
        return false;
    };

    const onActiveStepChange = (event) => {
        if (!Boolean(loggedIn)) return;
    };

    return (
        <SettingsPanelSection
            title='General'
            id='general-panel-section'
            action={
                <Button
                    size='small'
                    onClick={toggleAdvancedSettings}
                    startIcon={showAdvancedSettings ? <UncheckedBoxIcon /> : <CheckedBoxIcon />}
                    aria-checked={showAdvancedSettings ? 'false' : 'true'}
                >
                    Step Guide
                </Button>
            }
        >
            {showAdvancedSettings ? renderSettingsList() : renderSettingsStepper()}
            <LogoMediaPickerDialog />
            <BackgroundMediaPickerDialog />
        </SettingsPanelSection>
    );
};

const mapStateToProps = (state) => {
    const { 
        videoDirty,
        audioDirty,
        showAdvancedSettings
    } = state.create;

    const { loggedIn } = state.app;
    const { shape: visualizerShape } = state.createPreview.present.videoSettings.visualizer;
    const { savingInProgress } = state.create.videoSavingState;

    return {
        videoDirty,
        audioDirty,
        loggedIn,
        showAdvancedSettings,
        visualizerShape,
        savingInProgress
    }
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const openBackgroundPicker = () => dispatch(mediaPickerActions.setProperty('backgroundMediaPickerOpen', true));
    const openLogoPicker = () => dispatch(mediaPickerActions.setProperty('logoMediaPickerOpen', true));
    const setShowAdvancedSettings = (show) => dispatch(createActions.setShowAdvancedSettings(show));
    const openExportDialog = (navigate) => dispatch(createActions.openExportDialog(navigate));
    const showCriticalOperationProgressWarning = () => dispatch(createActions.showCriticalOperationProgressWarning());

    return {
        setProperty,
        openBackgroundPicker,
        openLogoPicker,
        setShowAdvancedSettings,
        openExportDialog,
        showCriticalOperationProgressWarning
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(SettingsPanelSectionGeneral, styles));